import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { FRIEND_MESSAGE_MAX_LEN, friendsChanged } from "@/lib/friends";
import Avatar from "@/components/Avatar";
import PlayerLink from "@/components/PlayerLink";
import { toast } from "sonner";
import { ArrowLeft, PaperPlaneRight } from "@phosphor-icons/react";

const POLL_MS = 3000;

function formatTime(ts) {
  try {
    const d = new Date(ts);
    const today = new Date().toDateString() === d.toDateString();
    return today
      ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      : d.toLocaleString([], { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

// One-to-one chat with a friend, outside any game (/friends/:id).
export default function FriendChat() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [friend, setFriend] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const atBottom = useRef(true);
  const lastSeen = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/friends/${id}/messages`);
      setFriend(data.friend);
      setMessages((prev) => {
        const pending = prev.filter((m) => m.pending);
        return [...data.messages, ...pending];
      });
      setError("");
      // Reading marks their messages read: refresh the header badge when
      // something new came in.
      const last = data.messages[data.messages.length - 1]?.id || "";
      if (last !== lastSeen.current) {
        lastSeen.current = last;
        friendsChanged();
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403 || status === 404) {
        setError(status === 403 ? "You can only chat with friends." : "Player not found.");
      }
    }
  }, [id]);

  useEffect(() => {
    setFriend(null);
    setMessages([]);
    setError("");
    atBottom.current = true;
    lastSeen.current = null;
    load();
    const t = setInterval(() => document.visibilityState !== "hidden" && load(), POLL_MS);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  // Follow new messages unless the player has scrolled up to read.
  useEffect(() => {
    const el = listRef.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const onScroll = () => {
    const el = listRef.current;
    if (el) atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    const tempId = `tmp-${Date.now()}`;
    atBottom.current = true;
    setMessages((m) => [...m, { id: tempId, from: user?.id, text, ts: new Date().toISOString(), pending: true }]);
    setDraft("");
    setSending(true);
    try {
      const { data } = await api.post(`/friends/${id}/messages`, { text });
      setMessages((m) => m.map((x) => (x.id === tempId ? data : x)));
    } catch (err) {
      setMessages((m) => m.filter((x) => x.id !== tempId));
      setDraft(text);
      toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't send that message");
    } finally {
      setSending(false);
    }
  };

  const back = () => navigate("/profile#friends");

  return (
    <main
      className="max-w-2xl mx-auto px-4 md:px-6 py-4 md:py-8 flex flex-col h-[calc(100dvh-4rem)]"
      data-testid="friend-chat-page"
    >
      <div className="flex-none flex items-center gap-3 mb-3">
        <button
          type="button"
          onClick={back}
          className="-ml-2 p-2 rounded-full text-muted-foreground hover:text-foreground"
          aria-label="Back to friends"
          data-testid="friend-chat-back"
        >
          <ArrowLeft size={18} weight="bold" />
        </button>
        {friend && (
          <>
            <Avatar userId={friend.id} username={friend.username} version={friend.avatar_v ?? null} className="w-9 h-9 text-sm" />
            <div className="min-w-0">
              <PlayerLink id={friend.id} className="font-display font-bold text-lg truncate block" testId="friend-chat-name">
                {friend.username}
              </PlayerLink>
              <span className="text-xs text-muted-foreground">{friend.elo_rating} Elo</span>
            </div>
          </>
        )}
      </div>

      <div className="flex-1 min-h-0 flex flex-col rounded-2xl border border-border bg-surface">
        <div
          ref={listRef}
          onScroll={onScroll}
          className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-1.5"
          data-testid="friend-chat-messages"
        >
          {error ? (
            <p className="text-sm text-muted-foreground text-center mt-8">{error}</p>
          ) : !friend ? (
            <p className="text-sm text-muted-foreground text-center mt-8">Loading…</p>
          ) : messages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center mt-8">No messages yet. Say hi!</p>
          ) : (
            messages.map((m, i) => {
              const mine = m.from === user?.id;
              const prev = messages[i - 1];
              const gap = !prev || new Date(m.ts) - new Date(prev.ts) > 15 * 60 * 1000;
              return (
                <React.Fragment key={m.id}>
                  {gap && (
                    <div className="text-[11px] text-muted-foreground text-center pt-2">{formatTime(m.ts)}</div>
                  )}
                  <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[80%] px-3 py-1.5 rounded-2xl text-sm break-words whitespace-pre-wrap ${
                        mine ? "bg-brand text-brand-foreground rounded-br-md" : "bg-muted text-foreground rounded-bl-md"
                      } ${m.pending ? "opacity-60" : ""}`}
                      title={formatTime(m.ts)}
                    >
                      {m.text}
                    </div>
                  </div>
                </React.Fragment>
              );
            })
          )}
        </div>

        <form onSubmit={send} className="flex-none flex items-center gap-2 p-2 border-t border-border">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, FRIEND_MESSAGE_MAX_LEN))}
            maxLength={FRIEND_MESSAGE_MAX_LEN}
            placeholder="Message"
            disabled={!friend || !!error}
            enterKeyHint="send"
            className="flex-1 min-w-0 h-10 px-4 rounded-full bg-background border border-border text-base md:text-sm
                       focus:outline-none focus:ring-2 focus:ring-brand/40 disabled:opacity-60"
            data-testid="friend-chat-input"
          />
          <button
            type="submit"
            disabled={!friend || !!error || !draft.trim()}
            className="flex-none w-10 h-10 rounded-full bg-brand text-brand-foreground flex items-center justify-center disabled:opacity-40"
            aria-label="Send"
            data-testid="friend-chat-send"
          >
            <PaperPlaneRight size={16} weight="fill" />
          </button>
        </form>
      </div>
    </main>
  );
}
